import { useEffect, useState } from "react";
import { Plus, Trash2, Save, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { loadLexique, saveLexique } from "@/lib/lexique";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

const EXAMPLES = [{ dit: "CCI", ecrit: "carcinome canalaire infiltrant" }, { dit: "", ecrit: "Ki-67" }];

export const LexiquePanel = () => {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ dit: "", ecrit: "" });
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  useEffect(() => { loadLexique(user?.id).then((e) => setRows([...e])); }, [user?.id]);
  const persist = async (next) => {
    setBusy(true);
    try { setRows([...(await saveLexique(next))]); toast.success("Lexique enregistré"); return true; } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; } finally { setBusy(false); }
  };
  const add = async () => {
    if (!form.ecrit.trim()) return;
    if (await persist([...rows, { dit: form.dit.trim(), ecrit: form.ecrit.trim() }])) setForm({ dit: "", ecrit: "" });
  };
  if (!rows) return <div className="text-sm text-slate-400">Chargement…</div>;
  return (
    <Card className="p-5 space-y-4" data-testid="lexique-panel">
      <div className="flex items-start gap-3">
        <BookOpen className="h-5 w-5 text-sky-600 mt-0.5" />
        <div className="text-sm text-slate-600">
          <div className="font-semibold text-slate-800">Mon lexique de dictée</div>
          Ajoutez vos termes médicaux pour mieux les faire reconnaître, et vos abréviations : quand vous dites « CCI », la dictée écrit « carcinome canalaire infiltrant ». Ce lexique est personnel.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
        <Input placeholder="Je dis… (optionnel, ex. CCI)" value={form.dit} onChange={(e) => setForm({ ...form, dit: e.target.value })} data-testid="lexique-dit-input" />
        <Input placeholder="La dictée écrit… (ex. carcinome canalaire infiltrant)" value={form.ecrit} onChange={(e) => setForm({ ...form, ecrit: e.target.value })}
               onKeyDown={(e) => e.key === "Enter" && add()} data-testid="lexique-ecrit-input" />
        <Button onClick={add} disabled={busy || !form.ecrit.trim()} data-testid="lexique-add-button" style={{ background: "#0284C7" }}><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="lexique-empty">
          Aucun terme pour l'instant. Exemples : {EXAMPLES.map((e) => (e.dit ? `« ${e.dit} » → ${e.ecrit}` : e.ecrit)).join(" · ")}
        </div>
      ) : (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="lexique-list">
          {rows.map((r, i) => (
            <div key={`${r.dit}|${r.ecrit}`} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`lexique-row-${i}`}>
              <span className="w-40 shrink-0 font-mono text-xs text-slate-500">{r.dit ? `« ${r.dit} »` : "terme"}</span>
              <span className="flex-1 text-slate-800">{r.ecrit}</span>
              <button type="button" disabled={busy} onClick={() => persist(rows.filter((_, j) => j !== i))} data-testid={`lexique-delete-${i}`}
                      className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[11px] text-slate-400 flex items-center gap-1"><Save className="h-3 w-3" /> Enregistré automatiquement · {rows.length}/200 entrées</div>
    </Card>
  );
};
